import { HTMLAttributes, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { useAddVoucherToMap, useVoucherMapValue } from 'hooks/business/conversation';
import { voucher as fetchVoucher } from 'services/api/conversation';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { formatNumber, parseNumber } from 'utils/numberFormat';
import { get } from 'lodash-es';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 384px;
  border-radius: 10px;
  background-color: #fdf3f5;
  mask: radial-gradient(circle at 8px 8px, transparent 8px, red 0px) -8px -49px;

  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    padding: 10px 20px;
    > span {
      flex: 0 0 28px;
      line-height: 28px;
      font-size: 16px;
      color: #f05b5b;
      margin-bottom: 4px;
    }
  }
  > footer {
    flex: 0 0 40px;
    line-height: 40px;
    padding: 0 20px;
    color: #84868c;
    font-size: 14px;
    border-top: 1px dashed #ffdfe1;
  }
`;

const DiscountAmount = styled.span`
  font-size: 22px;
`;

export interface VoucherMessageTiktokProps extends HTMLAttributes<HTMLDivElement> {
  voucherId: string;
  voucherCode: string;
}

function VoucherMessageTiktok(props: VoucherMessageTiktokProps) {
  const { voucherId, voucherCode, ...otherProps } = props;

  const { t } = useTranslate();

  const voucherMap = useVoucherMapValue();

  const addVoucherToMap = useAddVoucherToMap();

  const { currentConversation: conversation } = useChatConversationContext();

  const { [voucherId]: voucher } = voucherMap;

  const refsCurrent = {
    addVoucherToMap,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const { channel, storeId } = conversation || {};

  useEffect(() => {
    if (voucher || !channel || !storeId) {
      return;
    }

    fetchVoucher({
      internal_store_id: storeId,
      external_voucher_id: voucherId,
      platform: channel,
    })
      .then((res) => {
        if (!res) {
          throw new Error('Not found');
        }

        const {
          current: { addVoucherToMap },
        } = refs;

        addVoucherToMap(res, storeId);
      })
      .catch(() => {});
  }, [voucher, voucherId, channel, storeId, addVoucherToMap]);

  const { voucherType, rewardType, unit, discountAmount, percentage, minBasketPrice, maxPrice } =
    voucher || {};

  const showMaxPrice = useMemo(() => {
    return rewardType === 2 || rewardType === 3;
  }, [rewardType]);

  if (voucher === undefined) {
    return <Container {...otherProps} />;
  }

  return (
    <Container {...otherProps}>
      <section>
        <span>
          {
            {
              1:
                discountAmount || discountAmount === 0 ? (
                  <span>
                    <DiscountAmount>{discountAmount}</DiscountAmount>
                    {unit}
                  </span>
                ) : null,
              2: percentage ? <span>{formatNumber(Number(percentage) / 100, '0.00%')}</span> : null,
              3: percentage ? (
                <span>{formatNumber(Number(percentage) / 100, '0.00%')} Coins Cashback</span>
              ) : null,
            }[rewardType]
          }
        </span>
        {minBasketPrice ? (
          <div className="!text-[#515359] leading-[18px]">
            {`${t('voucher.最低消费金额')}: ${unit} ${formatNumber(minBasketPrice, '0,0.00')}`}
          </div>
        ) : null}
        {showMaxPrice ? (
          <div className="!text-[#515359] leading-[18px]">
            {`${t('form.maxPrice')}`}:
            {parseNumber(maxPrice)
              ? `${unit} ${formatNumber(maxPrice, '0,0.00')}`
              : t('voucher.maxPriceNoLimit')}
          </div>
        ) : null}
        {get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.type') === 'RELATIVE' ? (
          <div>
            {t('tiktok_voucher_day_validity', {
              count: get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.relativeTime'),
            })}
          </div>
        ) : (
          <div className="my-[5px] leading-[18px] text-[#84868c]">
            {t('marketing.有效期')}:
            {`${dayjs(
              Number(get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.startTime')) * 1000,
            ).format('DD/MM/YYYY')}-${dayjs(
              Number(get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.endTime')) * 1000,
            ).format('DD/MM/YYYY')}`}
          </div>
        )}
      </section>
      <footer>{voucherType === 1 ? t('aide.全部商品') : t('aide.指定商品')}</footer>
    </Container>
  );
}

export default VoucherMessageTiktok;
